import { StateContext } from "./Round";

import DisclosureInfo from "@client/components/round/DisclosureInfo";
import { RecusalInstructions } from "@client/components/round/Instructions";
import { Button } from "@client/components/utils/Button";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import Modal from "@client/components/utils/Modal";
import ProposalLink from "@client/components/utils/ProposalLink";
import { Tooltip } from "@client/components/utils/Tooltip";
import CloseIcon from "@client/icons/CloseIcon";
import DownChevron from "@client/icons/DownChevron";
import { Pencil } from "@client/icons/Pencil";
import { RecentIndicator } from "@client/utils/recentProposals";
import SlidingToggle from "@client/utils/SlidingToggle";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  evaluatorIsMeanRecommender,
  getEffectiveTrack,
  hasTrackWithMatchingStatus,
  isHold,
} from "@engine/round/accessors";
import type { RecusalVote } from "@engine/round/actions";
import type {
  Disclosure,
  Proposal,
  RoundAggregate,
  RoundMember,
} from "@engine/round/aggregate";
import { type Track, trackNames } from "@lib/types";
import { canView } from "@lib/utils/anonymization";
import { useLocalStorage } from "@uidotdev/usehooks";
import clsx from "clsx";
import { useContext, useEffect, useId, useState } from "react";
import { Redirect } from "wouter";
import { DispatchContext } from "./Round";

type RecusalInfo = {
  disclosure: string;
  selfRecused: boolean;
  votes: {
    [memberId: string]: RecusalVote;
  };
  decision: "recused" | "not-recused" | null;
};

type RecusalInfos = {
  [proposalId: string]: {
    [memberId: string]: RecusalInfo;
  };
};

function recusalInfosOfRound(
  round: RoundAggregate,
  membersToShow: RoundMember[],
): RecusalInfos {
  const trackDisclosures: RecusalInfos = {};

  for (const [proposalIdAndMemberId, maybeDisclosure] of Object.entries(
    round.proposalDisclosure,
  )) {
    if (!maybeDisclosure.hasDisclosure) {
      continue;
    }

    const [proposalId, memberId] = proposalIdAndMemberId.split("%");
    if (!membersToShow.some((m) => m.id === memberId)) {
      continue;
    }

    if (!trackDisclosures[proposalId]) {
      trackDisclosures[proposalId] = {};
    }

    const { disclosure, selfRecused, votes, decision } = maybeDisclosure;
    trackDisclosures[proposalId][memberId] = {
      disclosure,
      selfRecused,
      votes: Object.fromEntries(
        membersToShow
          .filter((m) => m.id !== memberId && m.id in votes)
          .map((m) => [m.id, votes[m.id]]),
      ),
      decision,
    };
  }

  return trackDisclosures;
}

const Recusals = withErrorBoundary(
  ({ params }: { params: { track: string } }) => {
    const context = useContext(StateContext);
    const dispatch = useContext(DispatchContext);
    const { round, actingAsUserId: userId, mode } = context;
    const [ordering, setOrdering] = useState<"application" | "recommender">(
      "application",
    );
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newDisclosureProposal, setNewDisclosureProposal] =
      useState<string>("");
    const userRoundMember = round.members[userId];
    const track = params.track.toUpperCase();
    const effectiveTrack = getEffectiveTrack(mode, userRoundMember);
    const allProposals = Object.values(round.proposalForms)
      .flatMap((form) => Object.values(form.proposals))
      .sort((a, b) => a.shortname.localeCompare(b.shortname));

    // Note that we set this on first render but not thereafter so rows don't
    // unexpectedly disappear when the user clicks yes or no.
    const [needsDisclosure] = useState<Proposal[]>(() => {
      const needsDisclosure: Proposal[] = [];

      for (const proposal of allProposals) {
        if (isHold(round, proposal)) {
          continue;
        }

        if (round.proposalDisclosure[`${proposal.id}%${userId}`] == null) {
          needsDisclosure.push(proposal);
        }
      }
      return needsDisclosure;
    });

    if (!(track in trackNames)) {
      return <div>Invalid track.</div>;
    }

    if (!canView(effectiveTrack, track as Track)) {
      return (
        <Redirect
          to={`~/round/${round.id}/recusals/${effectiveTrack.toLowerCase()}`}
          replace
        />
      );
    }

    const isAdmin = userIsAdmin(userId, context);
    const [instructionsVisible, setInstructionsVisible] = useLocalStorage(
      "instructionsVisible",
      true,
    );

    const accessiblePhases = [
      "2.initial-evaluations",
      "3.full-discussion",
      "4.evals-freeze",
      "5.complete",
    ];

    if (
      !isAdmin &&
      !hasTrackWithMatchingStatus(round, userId, mode, accessiblePhases)
    ) {
      return <Redirect to="/" />;
    }

    const membersWhoCanBeVotedOn = Object.values(round.members)
      .filter(
        (member) =>
          [track, "NO_TRACK"].includes(member.track) &&
          member.status === "active",
      )
      .sort((a, b) => a.initials.localeCompare(b.initials));

    // Different from membersWhoCanBeVotedOn in that they have to be a
    // recommender and in this track (not NO_TRACK).
    const votingMembers = Object.values(round.members)
      .filter(
        (member) =>
          member.track === track &&
          member.status === "active" &&
          Object.values(member.evaluators).some(
            (evaluator) =>
              evaluator.type === "recommender" &&
              !evaluatorIsMeanRecommender(evaluator),
          ),
      )
      .sort((a, b) => {
        // Sort the current user to the front
        if (a.id === userId) return -1;
        if (b.id === userId) return 1;
        return 0;
      });

    const trackDisclosures = recusalInfosOfRound(round, membersWhoCanBeVotedOn);

    const proposalsWithUserDisclosure = new Set(
      Object.entries(round.proposalDisclosure)
        .filter(
          ([key, d]) => key.endsWith(`%${userId}`) && d.hasDisclosure === true,
        )
        .map(([key]) => key.split("%")[0]),
    );

    const proposalsUserCouldAddRecusalFor = allProposals.filter(
      (p) => !isHold(round, p) && !proposalsWithUserDisclosure.has(p.id),
    );

    const handleDisclosureChange = (
      proposalId: string,
      memberId: string,
      disclosureInfo: {
        selfRecused: boolean;
        disclosure: string;
        hasDisclosure: boolean;
      },
    ) =>
      dispatch({
        type: "PROPOSAL_DISCLOSURE_CHANGE",
        proposalId,
        userId: memberId,
        ...disclosureInfo,
      });

    const voteRows: React.ReactNode[] = [];
    let rowsVotedOn = 0;
    if (ordering === "application") {
      for (const proposal of allProposals) {
        if (!trackDisclosures[proposal.id]) {
          continue;
        }

        const recusalRows: React.ReactNode[] = [];

        for (const [memberId, disclosure] of Object.entries(
          trackDisclosures[proposal.id],
        )) {
          const member = membersWhoCanBeVotedOn.find((m) => m.id === memberId);

          // There can be disclosures from members who are no longer active.
          if (!member) {
            continue;
          }

          const rowComplete =
            memberId === userId || disclosure.votes[userId] != null;
          rowsVotedOn += rowComplete ? 1 : 0;

          recusalRows.push(
            <RecusalRow
              votingMembers={votingMembers}
              key={`${proposal.id}-${memberId}`}
              subjectOfVote={member}
              userRoundMember={userRoundMember}
              proposal={proposal}
              recusalInfo={disclosure}
              editingKey={editingKey}
              setEditingKey={setEditingKey}
            />,
          );
        }
        voteRows.push(...recusalRows);
      }
    } else {
      for (const member of membersWhoCanBeVotedOn) {
        const memberRows: React.ReactNode[] = [];
        for (const proposal of allProposals) {
          if (
            !trackDisclosures[proposal.id] ||
            !trackDisclosures[proposal.id][member.id]
          ) {
            continue;
          }

          const disclosure = trackDisclosures[proposal.id][member.id];

          const rowComplete =
            member.id === userId || disclosure.votes[userId] != null;
          rowsVotedOn += rowComplete ? 1 : 0;

          memberRows.push(
            <RecusalRow
              votingMembers={votingMembers}
              key={`${proposal.id}-${member.id}`}
              subjectOfVote={member}
              userRoundMember={userRoundMember}
              proposal={proposal}
              recusalInfo={disclosure}
              editingKey={editingKey}
              setEditingKey={setEditingKey}
            />,
          );
        }

        voteRows.push(...memberRows);
      }
    }

    const voteTable =
      voteRows.length === 0 ? (
        <div className="py-4">No recusals found</div>
      ) : (
        <table className="border-separate border-spacing-0 light:bg-white dark:bg-stone-800 border-x border-b light:border-gray-300 dark:border-stone-700">
          <thead className="sticky top-0 z-20">
            <tr className="light:bg-gray-100 dark:bg-stone-700 border-y border-gray-300 dark:border-stone-700">
              <th className="py-2 px-4 border-y border-gray-300 dark:border-stone-700">
                Proposal
              </th>
              <th className="py-2 px-4 border-y border-gray-300 dark:border-stone-700">
                Recommender
              </th>
              <th className="py-2 px-4 border-y border-gray-300 dark:border-stone-700">
                <em>Result</em>
              </th>
              <th className="py-2 px-4 border-y border-gray-300 dark:border-stone-700">
                Disclosure
              </th>
              <th className="py-2 px-4 border-y border-gray-300 dark:border-stone-700">
                Self Recused
              </th>
              <th className="py-2 px-4 border-y border-gray-300 dark:border-stone-700" />
              <th
                className="py-2 px-4 border-y border-gray-300 dark:border-stone-700"
                colSpan={votingMembers.length}
              >
                Votes
              </th>
            </tr>
            <tr className="border-b light:bg-white dark:bg-stone-800 border-gray-300 dark:border-stone-700">
              <th
                colSpan={6} // proposal ... result
                className="border-b border-gray-300 dark:border-stone-700"
              />
              {votingMembers.map((member) => (
                <th
                  key={member.id}
                  className="py-2 px-4 border-b border-gray-300 dark:border-stone-700"
                >
                  {member.initials}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{voteRows}</tbody>
        </table>
      );

    let needsDisclosureRows: React.ReactNode[] = needsDisclosure.map(
      (proposal) => (
        <tr key={proposal.id}>
          <th
            className={clsx(
              "py-2 px-4 sticky left-0 text-left z-10",
              "border-r border-b light:border-gray-200 dark:border-stone-700",
              // index === 0 && "border-l",
              "light:bg-white dark:bg-stone-800",
            )}
          >
            <ProposalLink proposal={proposal}>
              {proposal.shortname}
              <RecentIndicator proposalId={proposal.id} />
            </ProposalLink>
          </th>
          <DisclosureInfo
            proposalId={proposal.id}
            memberId={userId}
            disclosure={round.proposalDisclosure[`${proposal.id}%${userId}`]}
            handleDisclosureChange={handleDisclosureChange}
          />
        </tr>
      ),
    );

    const needsDisclosureTable =
      needsDisclosureRows.length === 0 ? (
        <div className="py-4">No recusals found</div>
      ) : (
        <table className="border-separate border-spacing-0 light:bg-white dark:bg-stone-800 border-x border-b light:border-gray-300 dark:border-stone-700">
          <thead className="sticky top-0 z-20">
            <tr className="light:bg-gray-100 dark:bg-stone-700 border-y border-gray-300 dark:border-stone-700">
              <th className="py-2 px-4 border-y border-gray-300 dark:border-stone-700">
                Proposal
              </th>
              <th
                colSpan={2}
                className="py-2 px-4 border-y border-gray-300 dark:border-stone-700"
              >
                Disclosure
              </th>
            </tr>
          </thead>
          <tbody>{needsDisclosureRows}</tbody>
        </table>
      );

    return (
      <div className="p-4 pb-16 pr-16 w-screen h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold">Recusals</h1>
          <Button onClick={() => setAddModalOpen(true)}>
            Add New Disclosure
          </Button>
        </div>

        <button
          onClick={() => setInstructionsVisible(!instructionsVisible)}
          className="cursor-pointer"
        >
          <h2 className="text-2xl font-semibold">
            Instructions{" "}
            <DownChevron
              className={clsx(
                "inline w-5 h-5",
                !instructionsVisible && "rotate-180",
              )}
            />
          </h2>
        </button>

        {instructionsVisible && (
          <div className="flex flex-col xl:flex-row gap-4">
            <RecusalInstructions
              votedOn={rowsVotedOn}
              totalToVoteOn={voteRows.length}
              needsDisclosure={
                needsDisclosure.filter(
                  (p) => round.proposalDisclosure[`${p.id}%${userId}`] == null,
                ).length
              }
              totalProposals={allProposals.length}
              showTasks={effectiveTrack !== "NO_TRACK"}
              forOnboarding={false}
            />
          </div>
        )}

        {needsDisclosure.length > 0 && (
          <>
            <h2 className="text-2xl font-semibold">
              Proposals with no disclosure
            </h2>
            <div className="overflow-y-auto max-h-full mt-4">
              {needsDisclosureTable}
            </div>
          </>
        )}
        <h2 className="text-2xl font-semibold mt-4">Voting</h2>
        <div>
          Group by:{" "}
          <input
            type="radio"
            id="application"
            value="application"
            name="ordering"
            checked={ordering === "application"}
            onChange={() => setOrdering("application")}
          />{" "}
          <label htmlFor="application">Proposal</label>{" "}
          <input
            type="radio"
            id="recommender"
            value="recommender"
            name="ordering"
            checked={ordering === "recommender"}
            onChange={() => setOrdering("recommender")}
          />{" "}
          <label htmlFor="recommender">Recommender</label>
        </div>
        <div className="overflow-y-auto max-h-full mt-4">{voteTable}</div>
        {addModalOpen && (
          <Modal
            isOpen={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            className="min-w-md"
          >
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-semibold">Add Disclosure</h2>
              <select
                value={newDisclosureProposal}
                onChange={(e) => setNewDisclosureProposal(e.target.value)}
              >
                <option value="" disabled>
                  Select proposal
                </option>
                {proposalsUserCouldAddRecusalFor.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.shortname}
                  </option>
                ))}
              </select>
              <Button
                onClick={() => {
                  if (!newDisclosureProposal) return;
                  void handleDisclosureChange(newDisclosureProposal, userId, {
                    selfRecused: false,
                    disclosure: "",
                    hasDisclosure: true,
                  });
                  setEditingKey(`${newDisclosureProposal}%${userId}`);
                  setNewDisclosureProposal("");
                  setAddModalOpen(false);
                }}
                disabled={!newDisclosureProposal}
              >
                Add
              </Button>
            </div>
          </Modal>
        )}
      </div>
    );
  },
);

function RecusalRow({
  votingMembers,
  subjectOfVote,
  userRoundMember,
  proposal,
  recusalInfo,
  editingKey,
  setEditingKey,
}: {
  votingMembers: RoundMember[];
  subjectOfVote: RoundMember;
  userRoundMember: RoundMember;
  proposal: Proposal;
  recusalInfo: RecusalInfo;
  editingKey: string | null;
  setEditingKey: (k: string | null) => void;
}) {
  const dispatch = useContext(DispatchContext);
  const { round } = useContext(StateContext);
  const rowKey = `${proposal.id}%${subjectOfVote.id}`;
  const disclosure = round.proposalDisclosure[rowKey];
  const isEditing =
    editingKey === rowKey && subjectOfVote.id === userRoundMember.id;
  const userVote = recusalInfo.votes[userRoundMember.id];

  const handleDisclosureChange = (
    proposalId: string,
    memberId: string,
    disclosureInfo: {
      selfRecused: boolean;
      disclosure: string;
      hasDisclosure: boolean;
    },
  ) =>
    dispatch({
      type: "PROPOSAL_DISCLOSURE_CHANGE",
      proposalId,
      userId: memberId,
      ...disclosureInfo,
    });

  if (isEditing && disclosure) {
    return (
      <tr
        key={rowKey}
        className="border-b light:border-gray-300 dark:border-stone-700 text-sm hover:light:bg-gray-50 hover:dark:bg-stone-700"
      >
        <td className="py-2 px-4">
          <ProposalLink proposal={proposal}>
            {proposal.shortname}
            <RecentIndicator proposalId={proposal.id} />
          </ProposalLink>
        </td>
        <td className="py-2 px-4">{subjectOfVote.initials}</td>
        <UnchangeableVote
          vote={
            recusalInfo.decision === "recused"
              ? "y"
              : recusalInfo.decision === "not-recused"
                ? "n"
                : "tbd"
          }
        />
        <DisclosureEditor
          proposalId={proposal.id}
          memberId={subjectOfVote.id}
          disclosure={disclosure}
          handleDisclosureChange={handleDisclosureChange}
        />
        <td className="px-2">
          <button onClick={() => setEditingKey(null)}>
            <Tooltip title="Close Editor">
              <CloseIcon className="w-4 h-4" />
            </Tooltip>
          </button>
        </td>
        {votingMembers.map((votingMember) => {
          if (
            votingMember.id === userRoundMember.id &&
            userRoundMember.id !== subjectOfVote.id
          ) {
            return (
              <EditableVote
                key={votingMember.id}
                vote={recusalInfo.votes[votingMember.id]}
                proposalId={proposal.id}
                votingUserId={votingMember.id}
                subjectUserId={subjectOfVote.id}
              />
            );
          }

          const vote =
            subjectOfVote.id === votingMember.id
              ? recusalInfo.selfRecused
                ? "y"
                : "a"
              : recusalInfo.votes[votingMember.id];
          return <UnchangeableVote vote={vote} key={votingMember.id} />;
        })}
      </tr>
    );
  }

  return (
    <tr
      key={rowKey}
      className={clsx(
        "border-b light:border-gray-300 dark:border-stone-700 text-sm hover:light:bg-gray-50 hover:dark:bg-stone-700",
        userVote == null &&
          subjectOfVote.id !== userRoundMember.id &&
          "light:bg-yellow-100 dark:bg-yellow-600",
      )}
    >
      <td className="py-2 px-4">
        <ProposalLink proposal={proposal}>
          {proposal.shortname}
          <RecentIndicator proposalId={proposal.id} />
        </ProposalLink>
      </td>
      <td className="py-2 px-4">{subjectOfVote.initials}</td>
      <UnchangeableVote
        vote={
          recusalInfo.decision === "recused"
            ? "y"
            : recusalInfo.decision === "not-recused"
              ? "n"
              : "tbd"
        }
      />
      <td className="py-2 px-4 text-xs">
        {recusalInfo.disclosure}
        {subjectOfVote.id === userRoundMember.id && (
          <button
            className="ml-2"
            onClick={() => setEditingKey(rowKey)}
            title="Edit disclosure"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
      </td>
      <td
        className={clsx("py-2 px-4", {
          "light:bg-red-300 dark:bg-red-800": recusalInfo.selfRecused,
        })}
      >
        {recusalInfo.selfRecused ? "Yes" : "No"}
      </td>
      <td className="px-4" />
      {votingMembers.map((votingMember) => {
        if (
          votingMember.id === userRoundMember.id &&
          userRoundMember.id !== subjectOfVote.id
        ) {
          return (
            <EditableVote
              key={votingMember.id}
              vote={recusalInfo.votes[votingMember.id]}
              proposalId={proposal.id}
              votingUserId={votingMember.id}
              subjectUserId={subjectOfVote.id}
            />
          );
        }

        const vote =
          subjectOfVote.id === votingMember.id
            ? recusalInfo.selfRecused
              ? "y"
              : "a"
            : recusalInfo.votes[votingMember.id];
        return <UnchangeableVote vote={vote} key={votingMember.id} />;
      })}
    </tr>
  );
}

function UnchangeableVote({ vote }: { vote: RecusalVote | "tbd" | null }) {
  return (
    <td
      className={clsx("py-2 px-4", {
        "light:bg-green-300 dark:bg-green-600": vote === "n",
        "light:bg-red-300 dark:bg-red-800": vote === "y",
        "light:bg-gray-300 dark:bg-gray-600": vote === "a",
      })}
    >
      {vote === "y"
        ? "Yes"
        : vote === "n"
          ? "No"
          : vote === "a"
            ? "Abstain"
            : vote}
    </td>
  );
}

function EditableVote({
  vote,
  proposalId,
  votingUserId,
  subjectUserId,
}: {
  vote: RecusalVote | null;
  proposalId: string;
  votingUserId: string;
  subjectUserId: string;
}) {
  const dispatch = useContext(DispatchContext);

  return (
    <td
      className={clsx("py-2 px-4", {
        "light:bg-green-300 dark:bg-green-600": vote === "n",
        "light:bg-red-300 dark:bg-red-800": vote === "y",
        "light:bg-gray-300 dark:bg-gray-600": vote === "a",
      })}
    >
      <select
        value={vote ?? ""}
        onChange={(e) => {
          const vote = e.target.value as RecusalVote;
          void dispatch({
            type: "RECUSAL_VOTE",
            proposalId,
            votingUserId,
            subjectUserId,
            vote,
          });
        }}
      >
        <option value="" disabled>
          No Vote
        </option>
        <option value="y">Yes</option>
        <option value="n">No</option>
        <option value="a">Abstain</option>
      </select>
    </td>
  );
}

function DisclosureEditor({
  proposalId,
  memberId,
  disclosure,
  handleDisclosureChange,
}: {
  proposalId: string;
  memberId: string;
  disclosure: Disclosure;
  handleDisclosureChange: (
    proposalId: string,
    memberId: string,
    disclosureInfo: {
      selfRecused: boolean;
      disclosure: string;
      hasDisclosure: boolean;
    },
  ) => void;
}) {
  const id = useId();
  const [text, setText] = useState(disclosure.disclosure);

  useEffect(() => {
    setText(disclosure.disclosure);
  }, [disclosure.disclosure]);

  const handleToggleHasDisclosure = (isActive: boolean) => {
    handleDisclosureChange(proposalId, memberId, {
      selfRecused: disclosure.selfRecused,
      disclosure: text,
      hasDisclosure: isActive,
    });
  };

  const disclosureRadios = (
    <div className="flex items-center gap-1">
      <label className="text-sm">I have a disclosure:</label>
      <fieldset className="flex items-center gap-1">
        <div className="flex items-center gap-0.5">
          <input
            type="radio"
            id={`${id}-yes`}
            name={`${id}-disclosure`}
            value="yes"
            checked={disclosure.hasDisclosure === true}
            onChange={() => handleToggleHasDisclosure(true)}
          />
          <label htmlFor={`${id}-yes`}>Yes</label>
        </div>
        <div className="flex items-center gap-0.5">
          <input
            type="radio"
            id={`${id}-no`}
            name={`${id}-disclosure`}
            value="no"
            checked={disclosure.hasDisclosure === false}
            onChange={() => handleToggleHasDisclosure(false)}
          />
          <label htmlFor={`${id}-no`}>No</label>
        </div>
      </fieldset>
    </div>
  );

  if (!disclosure.hasDisclosure) {
    return (
      <>
        <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
          {disclosureRadios}
          <textarea
            className={clsx(
              "flex-1 min-w-96 border border-transparent rounded-md p-1",
              "max-h-0 overflow-hidden opacity-0 cursor-default",
            )}
          />
        </td>
        <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
          <div className="opacity-0 max-h-0 overflow-hidden">
            <SlidingToggle
              label="Self-recuse"
              state={false}
              onChange={() => {}}
              disabled
            />
          </div>
        </td>
      </>
    );
  }

  return (
    <>
      <td className="py-2 px-4 border-b light:border-gray-200 dark:border-stone-700">
        {disclosureRadios}
        <textarea
          className={clsx(
            "flex-1 min-w-96 border rounded-md p-1",
            "border light:border-stone-700 dark:bg-stone-900",
            "outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 light:focus:outline-blue-600 dark:focus:outline-blue-400",
          )}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() =>
            handleDisclosureChange(proposalId, memberId, {
              selfRecused: disclosure.selfRecused,
              disclosure: text,
              hasDisclosure: disclosure.hasDisclosure,
            })
          }
        />
      </td>
      <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
        <SlidingToggle
          label="Self-recuse"
          state={disclosure.selfRecused}
          onChange={(value) =>
            handleDisclosureChange(proposalId, memberId, {
              selfRecused: value,
              disclosure: text,
              hasDisclosure: disclosure.hasDisclosure,
            })
          }
        />
      </td>
    </>
  );
}

export default Recusals;
